import { LinkProps } from '@/types';
import { fullscreenLoadingMessages } from '@/constants/fullscreenLoadingMessages';
import * as R from '@/constants/systemRoutes';
import ProtectedRoute from './ProtectedRoute';
import React, { lazy, Suspense, useEffect } from 'react';
import { Container, FullscreenLoading } from '@/components';
import { AliveScope } from 'react-activation';
import { ModalFlexProvider, ModalAttributesProvider, useUserContext, CorporateFlagsProvider } from '@/hooks';
import { HashRouter, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { CartProvider, CartTotalsProvider, DiscountFlexAmountProvider, DiscountFlexProvider, PricesProvider, StockProvider } from '@/hooks';

interface RouteProps {
  id: string;
  path: string;
  hasCart: boolean;
  element: JSX.Element;
  isProtected: boolean;
}

const allRoutes = Object.values(R);

const routes = allRoutes
  .filter((r) => r.view)
  .map(({ id, link: path, view, isProtected, hasCart }: LinkProps): RouteProps => {
    const Element = lazy(async () => {
      return import(`@/views`).then((module) => ({
        default: module[view || 'NoMatch'],
      }));
    });

    return {
      id,
      path,
      hasCart: !!hasCart,
      element: (
        <Suspense
          fallback={
            <FullscreenLoading
              title={fullscreenLoadingMessages.routeSuspense.title}
              subtitle={fullscreenLoadingMessages.routeSuspense.subtitle}
            />
          }
        >
          <Element />
        </Suspense>
      ),
      isProtected: !!isProtected,
    };
  });

const cartRoutes = routes.filter((route) => route.hasCart);
const protectedRoutes = routes.filter((route) => route.isProtected && !route.hasCart);
const publicRoutes = routes.filter((route) => !route.isProtected && !route.hasCart);

const AppLayout: React.FC = () => (
  <PricesProvider>
    <StockProvider>
      <DiscountFlexProvider>
        <CartProvider>
          <DiscountFlexAmountProvider>
            <CartTotalsProvider>
              <AliveScope>
                <Container>
                  <Suspense
                    fallback={
                      <FullscreenLoading
                        title={fullscreenLoadingMessages.routeSuspense.title}
                        subtitle={fullscreenLoadingMessages.routeSuspense.subtitle}
                      />
                    }
                  >
                    <Outlet />
                  </Suspense>
                </Container>
              </AliveScope>
            </CartTotalsProvider>
          </DiscountFlexAmountProvider>
        </CartProvider>
      </DiscountFlexProvider>
    </StockProvider>
  </PricesProvider>
);

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { handleOfflineOrders } = useUserContext();

  useEffect(() => {
    handleOfflineOrders();
  }, [location]);

  return (
    <CorporateFlagsProvider>
      <Routes>
        <Route element={<AppLayout />}>
          {protectedRoutes.map((route) => (
            <Route
              key={route.id}
              id={route.id}
              path={route.path}
              element={
                <ProtectedRoute
                  requiredAccess={route.id === 'CHECKOUT' ? 'checkout' : route.id === 'SUCCESS' ? 'success' : route.id}
                  element={route.element}
                  key={route.id}
                />
              }
            />
          ))}

          {publicRoutes.map((route) => (
            <Route id={route.id} key={route.id} path={route.path} element={route.element} />
          ))}

          {cartRoutes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              id={route.id}
              element={
                <ProtectedRoute
                  // Só /sucesso tem guard aqui; /checkout segue sem, para o reload manter o RCA no pagamento.
                  requiredAccess={route.id === 'SUCCESS' ? 'success' : route.id}
                  key={route.id}
                  element={
                    <ModalFlexProvider>
                      <ModalAttributesProvider>
                        {route.element}
                      </ModalAttributesProvider>
                    </ModalFlexProvider>
                  }
                />
              }
            />
          ))}
        </Route>
      </Routes>
    </CorporateFlagsProvider>
  );
};

const CustomRoutes: React.FC = () => (
  <HashRouter>
    <AppRoutes />
  </HashRouter>
);

export default React.memo(CustomRoutes);
